// UCC tab — Baselayer-integrated rebuild. Header tiles + "File UCC-1"
// (Baselayer workspace flow when the user can draft Bgc filings, manual
// servicing record otherwise), filings table with the lapse countdown as
// the loudest column + source chips, per-filing drawer, and a bottom row
// pairing the Ucc monthly-report card with the lien-search card.

import { useState } from "react";
import { Plus } from "lucide-react";
import { Button, Card, SkeletonList } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import {
  useLoanUccFilings,
  useUccLienSearches,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { LoanReportsCard } from "@/components/portfolioAdmin/loanDetail/reports/LoanReportsCard";
import { UccHeaderStrip } from "../ucc/UccHeaderStrip";
import { ActiveFilingsTable } from "../ucc/ActiveFilingsTable";
import { UccFilingDrawer } from "../ucc/UccFilingDrawer";
import { LienSearchCard } from "../ucc/LienSearchCard";
import { FileUcc1FromLoanModal } from "../ucc/FileUcc1FromLoanModal";
import { ManualFileUcc1Modal } from "../ucc/ManualFileUcc1Modal";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";

interface UccTabProps {
  loan: LoanSummaryDto;
}

export function UccTab({ loan }: UccTabProps) {
  const auth = useAuth();
  const isAdmin = auth.hasRoleCode("ADMIN");
  const canFileInitial = auth.hasPermission("UCC.FileInitial") || isAdmin;
  const canBgcDraft =
    auth.hasPermission("BackgroundCheck.UccDraft") || isAdmin;

  const filingsQuery = useLoanUccFilings(loan.id);
  const searchesQuery = useUccLienSearches(loan.id);

  const filings = filingsQuery.data ?? [];
  const searches = searchesQuery.data ?? [];

  const [openFilingId, setOpenFilingId] = useState<string | null>(null);
  const [fileUccOpen, setFileUccOpen] = useState(false);

  const openFiling = openFilingId
    ? filings.find((f) => f.id === openFilingId) ?? null
    : null;

  // Never render a button that 403s: the Baselayer-workspace flow needs
  // BOTH the servicing FileInitial perm and the Bgc draft perm; servicing
  // FileInitial alone gets the manual record flow; neither = no button.
  const fileButton = canFileInitial ? (
    <Button
      variant="primary"
      onClick={() => setFileUccOpen(true)}
      data-testid="ucc-file-ucc1-button"
    >
      <Plus size={14} aria-hidden /> File UCC-1
    </Button>
  ) : null;

  return (
    <div className="flex flex-col gap-space-4" data-testid="ucc-tab">
      <UccHeaderStrip filings={filings} action={fileButton} />

      <Card>
        <h3 className="font-display font-bold text-h3 m-0 mb-space-3">
          Filings
        </h3>
        {filingsQuery.isLoading ? (
          <SkeletonList rows={3} data-testid="ucc-filings-loading" />
        ) : filingsQuery.isError ? (
          <p className="text-small text-status-error m-0" role="alert">
            Could not load UCC filings. Refresh to retry.
          </p>
        ) : filings.length === 0 ? (
          <p className="text-small text-gray-mid italic m-0">
            No UCC filings recorded for this loan.
          </p>
        ) : (
          <ActiveFilingsTable
            filings={filings}
            onRowClick={(f) => setOpenFilingId(f.id)}
          />
        )}
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-space-4 items-start">
        <LoanReportsCard loan={loan} discipline="Ucc" />
        <LienSearchCard
          loan={loan}
          searches={searches}
          isLoading={searchesQuery.isLoading}
          isError={searchesQuery.isError}
        />
      </div>

      {openFiling && (
        <UccFilingDrawer
          loanId={loan.id}
          filing={openFiling}
          allFilings={filings}
          open={!!openFiling}
          onClose={() => setOpenFilingId(null)}
        />
      )}
      {fileUccOpen &&
        (canBgcDraft ? (
          <FileUcc1FromLoanModal
            loan={loan}
            open={fileUccOpen}
            onClose={() => setFileUccOpen(false)}
          />
        ) : (
          <ManualFileUcc1Modal
            loanId={loan.id}
            open={fileUccOpen}
            onClose={() => setFileUccOpen(false)}
          />
        ))}
    </div>
  );
}
